// MeetingDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Tooltip as ChartTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Chart as ChartJS,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Stack,
    Chip,
    TextField,
    MenuItem,
    IconButton,
    Button,
    Skeleton,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CrudService from "services/cruds-service";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";
import { Link as RouterLink } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import isoWeek from "dayjs/plugin/isoWeek";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, Legend);

const BUCKETS = [
    { key: "0-30", label: "0-30 Day", color: "#2e7d32" },
    { key: "31-60", label: "31-60 Day", color: "#fbc02d" },
    { key: "61-90", label: "61-90 Day", color: "#ef6c00" },
    { key: "90+", label: "90+ Day", color: "#b71c1c" },
    { key: "no-date", label: "No Date", color: "#9e9e9e" },
];

const ellipsis = (s = "", max = 40) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

function colorOfBucket(key) {
    return BUCKETS.find((b) => b.key === key)?.color || "#9e9e9e";
}

function hexToRgb(hex) {
    const h = hex.replace("#", "");
    const bigint = parseInt(h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
}
function alpha(hex, a) {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function Th({ children, align = "left" }) {
    return (
        <th
            style={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                textAlign: align,
                padding: 10,
                borderBottom: "1px solid #e9ecef",
                background: "#fff",
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 0.2,
            }}
        >
            {children}
        </th>
    );
}

function Td({ children, align = "left" }) {
    return (
        <td style={{ padding: 10, borderBottom: "1px solid #f1f3f5", textAlign: align, fontSize: 13 }}>
            {children}
        </td>
    );
}

function formatDate(dt) {
    if (!dt) return "—";
    const d = dt instanceof Date ? dt : new Date(dt);
    return isNaN(d) ? "—" : d.toLocaleString();
}

const getPath = (obj, path) => path.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), obj);
const pick = (obj, keys) => {
    for (const k of keys) {
        const v = getPath(obj, k);
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return undefined;
};

function parseDateSafe(raw) {
    if (!raw) return null;
    if (raw instanceof Date && !isNaN(raw)) return raw;

    if (typeof raw === "object") {
        if (raw.$date !== undefined) {
            const v =
                typeof raw.$date === "object" && raw.$date.$numberLong
                    ? Number(raw.$date.$numberLong)
                    : raw.$date;
            return parseDateSafe(v);
        }
        if (typeof raw.seconds === "number") {
            const ms = raw.seconds * 1000 + (raw.nanoseconds ? raw.nanoseconds / 1e6 : 0);
            const d = new Date(ms);
            return isNaN(d) ? null : d;
        }
        if (typeof raw.valueOf === "function") {
            const v = raw.valueOf();
            if (v instanceof Date && !isNaN(v)) return v;
        }
    }

    if (typeof raw === "number") {
        const ms = raw > 1e12 ? raw : raw * 1000;
        const d = new Date(ms);
        return isNaN(d) ? null : d;
    }

    const s = String(raw).trim();
    const d = new Date(s);
    return isNaN(d) ? null : d;
}

function daysBetweenToday(raw) {
    const d = parseDateSafe(raw);
    if (!d) return null;
    const today = new Date();
    const diff = today - d;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function bucketOf(days) {
    if (days == null) return "no-date";
    if (days <= 30) return "0-30";
    if (days <= 60) return "31-60";
    if (days <= 90) return "61-90";
    return "90+";
}

function displayNames(list) {
    if (!Array.isArray(list)) return [];
    return list
        .map((u) => {
            if (!u) return "";
            if (typeof u === "string") return u;
            return (
                u.name ||
                u.fullName ||
                (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : "") ||
                u.email ||
                u.username ||
                String(u._id || "")
            );
        })
        .filter(Boolean);
}

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

function toExcelData(rows) {
    return rows.map((r) => ({
        Customer: r.customer || "",
        "Meeting Title": r.meetingTitle || "",
        Type: r.meetingType || "",
        Status: r.meetingStatus || "",
        Platform: r.meetingPlatform || "",
        "Last Meeting": r.meetingDate ? new Date(r.meetingDate).toLocaleString() : "",
        Days: Number.isFinite(r.days) ? r.days : "",
        "Meeting Owner": (r.salesRepNames || []).join("; "),
        Range: r.bucket || "",
        Country: r.country || "",
        City: r.city || "",
    }));
}

function downloadExcel(rows) {
    const data = toExcelData(rows);
    const ws = XLSX.utils.json_to_sheet(data, { skipHeader: false });

    const headers = Object.keys(data[0] || {});
    ws["!cols"] = headers.map((h) => ({
        wch: Math.max(h.length, ...data.map((r) => String(r[h] || "").length)) + 2,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Meetings");
    XLSX.writeFile(wb, `meeting-dashboard_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

const statusStyle = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "completed") return { bg: "#e8f5e9", fg: "#2e7d32", br: "#a5d6a7" };
    if (s === "planned") return { bg: "#e3f2fd", fg: "#1565c0", br: "#90caf9" };
    if (s === "cancelled" || s === "canceled") return { bg: "#ffebee", fg: "#c62828", br: "#ef9a9a" };
    if (s === "postponed") return { bg: "#fff8e1", fg: "#ef6c00", br: "#ffd180" };
    return { bg: "#eceff1", fg: "#455a64", br: "#cfd8dc" };
};

const typeStyle = (type) => {
    const t = String(type || "").toLowerCase();
    if (t.includes("new")) return { bg: "#f3e5f5", fg: "#6a1b9a", br: "#ce93d8" };
    if (t.includes("first") || t.includes("cold")) return { bg: "#f1f8e9", fg: "#558b2f", br: "#c5e1a5" };
    if (t.includes("retention")) return { bg: "#fff3e0", fg: "#ef6c00", br: "#ffcc80" };
    if (t.includes("partner")) return { bg: "#e8eaf6", fg: "#3949ab", br: "#9fa8da" };
    return { bg: "#f5f5f5", fg: "#616161", br: "#e0e0e0" };
};

// ✅ Donut click: single / multi / all
function applyChartClickSelection(label, prev, evt) {
    const multi = !!(evt?.native?.ctrlKey || evt?.native?.metaKey || evt?.native?.shiftKey);

    // Multi: toggle
    if (multi) {
        return prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label];
    }

    // Single: same click -> clear (ALL), else set single
    if (prev.length === 1 && prev[0] === label) return [];
    return [label];
}

export default function MeetingDashboard() {
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    const [search, setSearch] = useState("");
    const [type, setType] = useState("all");
    const [range, setRange] = useState("all");

    const [activeCustomer, setActiveCustomer] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [selSalesReps, setSelSalesReps] = useState([]);
    const [selTypes, setSelTypes] = useState([]);
    const [selStatuses, setSelStatuses] = useState([]);

    // ✅ new: country / city selections
    const [selCountries, setSelCountries] = useState([]);
    const [selCities, setSelCities] = useState([]);

    const [tblCustomer, setTblCustomer] = useState("");
    const [tblStatus, setTblStatus] = useState("");
    const [tblRep, setTblRep] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [tableMode, setTableMode] = useState("health");
    const [selWeeks, setSelWeeks] = useState([]);
    const isActiveRange = (key) => range === key;

    const applyDashboardFilter = ({
                                      range = "all",
                                      weeks = [],
                                      countries = [],
                                      cities = [],
                                      statuses = [],
                                      customer = "",
                                  } = {}) => {
        // reset
        setRange(range);
        setSelWeeks(weeks);
        setSelCountries(countries);
        setSelCities(cities);
        setSelStatuses(statuses);

        setActiveCustomer(customer);
        setTableMode("health");

        scrollToTable();
    };
    const onTotalMeetingClick = () => {
        applyDashboardFilter({ range: "all" });
    };
    const onRecentClick = () => {
        applyDashboardFilter({ range: "0-30" });
    };
    const onCriticalClick = () => {
        applyDashboardFilter({ range: "90+" });
    };
    const onNoDateClick = () => {
        applyDashboardFilter({ range: "no-date" });
    };


    // ✅ normalize (country/city/statusKey eklendi)
    const normalized = useMemo(() => {
        return (meetings || []).map((m, idx) => {
            const customer = pick(m, ["customer", "customerName", "company", "client", "account", "name"]) || "";
            const meetingTitle = pick(m, ["meetingTitle", "title", "subject"]) || "";
            const meetingType = pick(m, ["meetingType", "type"]) || "";
            const meetingStatus = pick(m, ["meetingStatus", "status"]) || "";
            const meetingPlatform = pick(m, ["meetingPlatform", "platform"]) || "";
            const meetingDate = parseDateSafe(m?.meetingDate);
            const days = daysBetweenToday(meetingDate);
            const bucket = bucketOf(days);

            const salesRep = pick(m, ["salesRep", "salesReps", "owners"]) || [];
            const attendees = pick(m, ["attendees", "participants"]) || [];
            const salesRepNames = displayNames(Array.isArray(salesRep) ? salesRep : [salesRep]);
            const attendeeNames = displayNames(Array.isArray(attendees) ? attendees : [attendees]);

            const industryArr = Array.isArray(m?.industry) ? m.industry : m?.industry ? [m.industry] : [];

            const isPartner =
                (meetingType || "").toLowerCase().includes("partner") ||
                customer.toLowerCase().includes("partner") ||
                industryArr.some((it) => String(it || "").toLowerCase().includes("partner"));

            const countryKey = String(pick(m, ["customerCountry", "country"]) || "Unknown").trim() || "Unknown";
            const cityKey = String(pick(m, ["city", "meetingCity"]) || "Unknown").trim() || "Unknown";
            const statusKey = String(meetingStatus || "Unknown").trim() || "Unknown";


            return {
                id: m?.id || m?._id || idx,
                raw: m,
                customer,
                taxId: m?.taxId || "",
                salesRepNames,
                attendeeNames,
                representatives: m?.representatives || "",
                representativesTitle: m?.representativesTitle || "",
                representativesContact: m?.representativesContact || "",
                webSite: m?.webSite || "",
                industry: Array.isArray(m?.industry) ? m.industry.filter(Boolean) : m?.industry ? [m.industry] : [],
                companySize: m?.companySize || "",
                logisticsDecision: m?.logisticsDecision || "",
                preMeetingTitle: m?.preMeetingTitle || "",
                preMeetingNotes: m?.preMeetingNotes || "",
                meetingTitle,
                meetingDate,
                meetingTime: m?.meetingTime || "",
                meetingDuration: m?.meetingDuration || "",
                meetingType,
                meetingStatus,
                statusKey, // ✅
                participants: Array.isArray(m?.participants) ? m.participants : [],
                meetingPlatform,
                meetingNotes: m?.meetingNotes || "",
                recapMeetingNotes: m?.recapMeetingNotes || "",
                recapCompleted: m?.recapCompleted || "",
                created_at: m?.created_at,
                updated_at: m?.updated_at,
                type: isPartner ? "partner" : "customer",
                days,
                bucket,
                color: colorOfBucket(bucket),
                country: countryKey, // ✅
                city: cityKey, // ✅
            };
        });
    }, [meetings]);

    // table scroll
    const tableRef = useRef(null);
    const scrollToTable = () => {
        if (tableRef.current) tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // fetch
    const fetchMeetings = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const res = await CrudService.getMeetings();
            const raw = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
            const list = raw.map((item) => (item && item.attributes ? { id: item.id, ...item.attributes } : item || {}));
            setMeetings(list);
        } catch (err) {
            console.error("Meetings fetch error:", err);
            setErrorMsg("An error occurred while retrieving meeting data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeetings();
    }, []);

    // options
    const allSalesReps = useMemo(() => uniq(normalized.flatMap((r) => r.salesRepNames)), [normalized]);
    const allTypes = useMemo(() => uniq(normalized.map((r) => r.meetingType)), [normalized]);
    const allStatuses = useMemo(() => uniq(normalized.map((r) => r.statusKey)), [normalized]);

    // ✅ meeting-level filtered dataset (country/city/status donut dahil)
    const meetingFiltered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const hasStart = dayjs.isDayjs(startDate) && startDate.isValid();
        const hasEnd = dayjs.isDayjs(endDate) && endDate.isValid();
        const start = hasStart ? startDate.startOf("day") : null;
        const end = hasEnd ? endDate.endOf("day") : null;

        return normalized.filter((r) => {
            const matchText =
                q === "" ||
                (r.customer || "").toLowerCase().includes(q) ||
                (r.taxId || "").toLowerCase().includes(q) ||
                (r.meetingTitle || "").toLowerCase().includes(q) ||
                (r.meetingType || "").toLowerCase().includes(q) ||
                (r.statusKey || "").toLowerCase().includes(q) ||
                (r.meetingPlatform || "").toLowerCase().includes(q) ||
                (r.industry || []).join(" ").toLowerCase().includes(q) ||
                (r.salesRepNames || []).join(" ").toLowerCase().includes(q) ||
                (r.attendeeNames || []).join(" ").toLowerCase().includes(q) ||
                (r.participants || []).join(" ").toLowerCase().includes(q) ||
                (r.country || "").toLowerCase().includes(q) ||
                (r.city || "").toLowerCase().includes(q);

            const matchType =
                type === "all" ||
                (type === "customer" && r.type === "customer") ||
                (type === "partner" && r.type === "partner");

            const matchSalesRep = selSalesReps.length === 0 || r.salesRepNames.some((n) => selSalesReps.includes(n));
            const matchMeetingType = selTypes.length === 0 || (r.meetingType && selTypes.includes(r.meetingType));
            const matchStatus = selStatuses.length === 0 || (r.statusKey && selStatuses.includes(r.statusKey));

            // ✅ donut filters
            const matchCountry = selCountries.length === 0 || selCountries.includes(r.country);
            const matchCity = selCities.length === 0 || selCities.includes(r.city);

            // date
            let matchDate = true;
            if ((start || end) && r.meetingDate instanceof Date && !isNaN(r.meetingDate)) {
                const d = dayjs(r.meetingDate);
                if (start && d.isBefore(start)) matchDate = false;
                if (end && d.isAfter(end)) matchDate = false;
            } else if (start || end) {
                matchDate = false;
            }
            const matchWeek =
                selWeeks.length === 0 ||
                (r.meetingDate &&
                    selWeeks.includes(dayjs(r.meetingDate).format("GGGG-[W]WW")));


            return (
                matchText &&
                matchType &&
                matchSalesRep &&
                matchMeetingType &&
                matchStatus &&
                matchCountry &&
                matchCity &&
                matchWeek &&
                matchDate
            );
        });
    }, [
        normalized,
        search,
        type,
        selSalesReps,
        selTypes,
        selStatuses,
        selCountries,
        selCities,
        selWeeks,
        startDate,
        endDate,
    ]);
    const kpiBase = useMemo(() => {
        if (range === "all") return meetingFiltered;

        if (range === "no-date") {
            return meetingFiltered.filter(r => r.meetingDate == null);
        }

        return meetingFiltered.filter(r => r.bucket === range);
    }, [meetingFiltered, range]);

    const chartBase = kpiBase; // KPI + tüm chartlar için tek source

    // ✅ Weekly meetings by meetingDate
    const weeklyMeetings = useMemo(() => {
        const map = {};

        chartBase.forEach((r) => {   // ✅ KPI filtresi aktif
            if (!r.meetingDate) return;
            const week = dayjs(r.meetingDate).format("GGGG-[W]WW");
            map[week] = (map[week] || 0) + 1;
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [chartBase]);


    const weeklyMeetingChartData = useMemo(
        () => ({
            labels: weeklyMeetings.map((w) => w.label),
            datasets: [
                {
                    label: "Meetings",
                    data: weeklyMeetings.map((w) => w.count),
                    borderRadius: 8,
                    backgroundColor: alpha("#1976d2", 0.45),
                    borderColor: "#1976d2",
                    borderWidth: 1.5,
                    maxBarThickness: 36,
                },
            ],
        }),
        [weeklyMeetings]
    );

    const weeklyMeetingChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.label}: ${ctx.parsed.y} meeting(s)`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                },
            },
            y: {
                beginAtZero: true,
                ticks: { precision: 0 },
                grid: { color: alpha("#000000", 0.06) },
            },
        },
    };


    const completedMeetingsFiltered = useMemo(() => {
        return meetingFiltered.filter((r) => String(r.statusKey || "").toLowerCase() === "completed");
    }, [meetingFiltered]);

    // ✅ last completed meeting per customer (health table dataset)
    const lastCompletedMeetingsByCustomer = useMemo(() => {
        const map = new Map();

        completedMeetingsFiltered.forEach((r) => {
            if (!r.customer) return;
            const existing = map.get(r.customer);
            if (!existing || (r.meetingDate && existing.meetingDate && r.meetingDate > existing.meetingDate)) {
                map.set(r.customer, r);
            }
            if (!existing && r.meetingDate) {
                map.set(r.customer, r);
            }
        });

        return Array.from(map.values());
    }, [completedMeetingsFiltered]);

    // ✅ range (bucket) filter health list
    const preCustomerFiltered = useMemo(() => {
        if (range === "all") return lastCompletedMeetingsByCustomer;
        return lastCompletedMeetingsByCustomer.filter((r) => r.bucket === range);
    }, [lastCompletedMeetingsByCustomer, range]);

    // ✅ country/city donut stats (health list üzerinden)
    const countryStats = useMemo(() => {
        const counts = new Map();
        preCustomerFiltered.forEach((r) => {
            const key = r.country || "Unknown";
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        return Array.from(counts.entries())
            .map(([country, count]) => ({ country, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    }, [preCustomerFiltered]);

    const cityStats = useMemo(() => {
        const counts = new Map();
        preCustomerFiltered.forEach((r) => {
            const key = r.city || "Unknown";
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        return Array.from(counts.entries())
            .map(([city, count]) => ({ city, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    }, [preCustomerFiltered]);

    // ✅ status donut stats (meeting-level üzerinden, filtreli)
    const statusStats = useMemo(() => {
        const counts = new Map();
        meetingFiltered.forEach((r) => {
            const key = r.statusKey || "Unknown";
            counts.set(key, (counts.get(key) || 0) + 1);
        });

        return Array.from(counts.entries())
            .map(([status, count]) => ({ status, count }))
            .sort((a, b) => b.count - a.count);
    }, [meetingFiltered]);

    // ✅ bucket stats (health list üzerinden)
    const customerBucketStats = useMemo(() => {
        const stats = { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0, "no-date": 0 };
        lastCompletedMeetingsByCustomer.forEach((r) => {
            stats[r.bucket] = (stats[r.bucket] || 0) + 1;
        });
        return stats;
    }, [lastCompletedMeetingsByCustomer]);

    const bucketStats = useMemo(() => {
        const byBucket = BUCKETS.reduce((acc, b) => ({ ...acc, [b.key]: 0 }), {});
        for (const r of preCustomerFiltered) byBucket[r.bucket] = (byBucket[r.bucket] || 0) + 1;
        const total = preCustomerFiltered.length;
        return { byBucket, total };
    }, [preCustomerFiltered]);

    const totalMeetings = meetingFiltered.length;

    // KPI
    const kpis = useMemo(() => {
        const daysVals = kpiBase
            .map(r => r.days)
            .filter(d => Number.isFinite(d));

        const avg =
            daysVals.length > 0
                ? Math.round(daysVals.reduce((a, b) => a + b, 0) / daysVals.length)
                : "—";

        return {
            total: kpiBase.length,
            within30: kpiBase.filter(r => r.bucket === "0-30").length,
            over90: kpiBase.filter(r => r.bucket === "90+").length,
            noDate: kpiBase.filter(r => r.meetingDate == null).length,
            avgDays: avg,
        };
    }, [kpiBase]);


    // bar / doughnut for buckets
    const barData = useMemo(() => {
        const labels = BUCKETS.map((b) => b.label);
        const data = BUCKETS.map((b) => bucketStats.byBucket[b.key]);
        const backgroundColor = BUCKETS.map((b) => alpha(b.color, 0.32));
        const borderColor = BUCKETS.map((b) => b.color);

        return {
            labels,
            datasets: [
                {
                    label: "Meeting Number",
                    data,
                    backgroundColor,
                    borderColor,
                    borderWidth: 1,
                    borderRadius: 8,
                    maxBarThickness: 40,
                },
            ],
        };
    }, [bucketStats]);

    const doughnutData = useMemo(() => {
        const labels = BUCKETS.map((b) => b.label);
        const data = BUCKETS.map((b) => bucketStats.byBucket[b.key]);
        const backgroundColor = BUCKETS.map((b) => alpha(b.color, 0.75));
        const borderColor = BUCKETS.map(() => "#fff");
        return { labels, datasets: [{ data, backgroundColor, borderColor, borderWidth: 2 }] };
    }, [bucketStats]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${ctx.formattedValue} record` } },
        },
        scales: {
            x: { grid: { color: alpha("#000000", 0.06) } },
            y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: alpha("#000000", 0.06) } },
        },
    };

    // ✅ customer chart: meetingFiltered (country/city/status filtreleri etkiler)
    const totalMeetingsByCustomer = useMemo(() => {
        const counts = new Map();
        chartBase.forEach((r) => {   // ✅ artık KPI filtresine bağlı
            if (!r.customer) return;
            counts.set(r.customer, (counts.get(r.customer) || 0) + 1);
        });

        return Array.from(counts.entries())
            .map(([customer, count]) => ({ customer, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 15);
    }, [chartBase]);


    const customerLabelsFull = useMemo(() => totalMeetingsByCustomer.map((x) => x.customer), [totalMeetingsByCustomer]);
    const customerLabelsShort = useMemo(() => customerLabelsFull.map((s) => ellipsis(s, 40)), [customerLabelsFull]);

    const customerChartData = useMemo(
        () => ({
            labels: customerLabelsShort,
            datasets: [
                {
                    label: "Total Meetings",
                    data: totalMeetingsByCustomer.map((x) => x.count),
                    backgroundColor: alpha("#0288d1", 0.3),
                    borderColor: "#0288d1",
                    borderWidth: 1,
                    borderRadius: 8,
                    maxBarThickness: 28,
                },
            ],
        }),
        [totalMeetingsByCustomer, customerLabelsShort]
    );

    const customerHoverPlugin = useMemo(
        () => ({
            id: "customerHoverTitle",
            afterEvent(chart, args) {
                const e = args.event;
                if (!e) return;
                const items = chart.getElementsAtEventForMode(e, "nearest", { axis: "y", intersect: false }, false);
                const idx = items?.[0]?.index;
                const full = typeof idx === "number" ? customerLabelsFull[idx] : "";
                if (chart.canvas) chart.canvas.title = full || "";
            },
        }),
        [customerLabelsFull]
    );

    const customerChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items) => customerLabelsFull[items[0].dataIndex],
                        label: (ctx) => `Meetings: ${ctx.formattedValue}`,
                    },
                },
                interaction: { mode: "nearest", axis: "y", intersect: false },
            },
            scales: {
                x: { ticks: { precision: 0 }, beginAtZero: true, grid: { color: alpha("#000000", 0.06) } },
                y: { grid: { display: false } },
            },
        }),
        [customerLabelsFull]
    );

    // clicks
    const handleBucketClick = (elements) => {
        const idx = elements?.[0]?.index;
        if (idx === undefined) return;
        const bucketKey = BUCKETS[idx].key;
        setRange((prev) => (prev === bucketKey ? "all" : bucketKey));
        scrollToTable();
    };

    const handleCustomerBarClick = (elements) => {
        const idx = elements?.[0]?.index;
        if (idx === undefined) return;
        const label = customerLabelsFull[idx];
        setActiveCustomer((prev) => (prev === label ? "" : label));
        setTableMode("total");
        scrollToTable();
    };

    // ✅ table rows: health => preCustomerFiltered, total => meetingFiltered
    const tableRows = useMemo(() => {
        if (tableMode === "health") return preCustomerFiltered;
        if (tableMode === "total" && activeCustomer) {
            return meetingFiltered.filter((r) => r.customer === activeCustomer);
        }
        return [];
    }, [tableMode, preCustomerFiltered, meetingFiltered, activeCustomer]);

    const tableFiltered = useMemo(() => {
        const c = tblCustomer.trim().toLowerCase();
        const s = tblStatus.trim().toLowerCase();
        const r = tblRep.trim().toLowerCase();

        return tableRows.filter((row) => {
            const okC = !c || (row.customer || "").toLowerCase().includes(c);
            const okS = !s || (row.meetingStatus || "").toLowerCase() === s;
            const okR = !r || row.salesRepNames.some((n) => n.toLowerCase().includes(r));
            return okC && okS && okR;
        });
    }, [tableRows, tblCustomer, tblStatus, tblRep]);

    const sortedTableRows = useMemo(() => {
        return [...tableFiltered].sort((a, b) => {
            const da = Number.isFinite(a.days) ? a.days : -Infinity;
            const db = Number.isFinite(b.days) ? b.days : -Infinity;
            return db - da;
        });
    }, [tableFiltered]);

    const pagedRows = useMemo(
        () => sortedTableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedTableRows, page, rowsPerPage]
    );

    useEffect(() => {
        setPage(0);
    }, [tableRows, tblCustomer, tblStatus, tblRep]);

    const renderChips = (selectedArray, onDelete) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectedArray.map((val) => (
                <Chip
                    key={val}
                    size="small"
                    label={val}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDelete={() => onDelete(val)}
                />
            ))}
        </Box>
    );

    const tblInputSx = {
        minWidth: 180,
        "& .MuiOutlinedInput-root": { height: 40, borderRadius: 2 },
        "& .MuiInputBase-input": {
            padding: "8.5px 14px",
            fontSize: 13,
            height: "auto",
            boxSizing: "border-box",
        },
        "& .MuiSelect-select": {
            padding: "8.5px 14px",
            display: "flex",
            alignItems: "center",
            height: "auto",
            boxSizing: "border-box",
        },
        "& .MuiInputLabel-root": { fontSize: 13, top: "-2px" },
    };

    const unifiedInputSx = {
        minWidth: 180,
        "& .MuiOutlinedInput-root": { height: 40, borderRadius: "10px", backgroundColor: "#f5f6f8" },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d5dd" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#b0b7c3" },
        "& .MuiInputBase-input": { padding: "8.5px 14px", fontSize: 13, boxSizing: "border-box" },
        "& .MuiSelect-select": { padding: "8.5px 14px", display: "flex", alignItems: "center", boxSizing: "border-box" },
        "& .MuiInputLabel-root": { fontSize: 13, color: "#667085" },
    };

    const actionBtnSx = {
        textTransform: "none",
        borderRadius: 2,
        px: 1.25,
        boxShadow: "none",
    };
    const cleanBtnSx = {
        ...actionBtnSx,
        bgcolor: "#546e7a",
        color: "#fff",
        "&:hover": { bgcolor: "#455a64" },
    };
    const downloadBtnSx = {
        ...actionBtnSx,
        bgcolor: "#2e7d32",
        color: "#fff",
        "&:hover": { bgcolor: "#1b5e20" },
    };

    const COLOR_PALETTE = [
        "#1976d2",
        "#2e7d32",
        "#f57c00",
        "#d32f2f",
        "#7b1fa2",
        "#0288d1",
        "#388e3c",
        "#fbc02d",
        "#c2185b",
        "#0097a7",
        "#8e24aa",
        "#512da8",
        "#455a64",
        "#9e9d24",
        "#5d4037",
    ];

    // donut chart datas
    const countryChartData = useMemo(
        () => ({
            labels: countryStats.map((x) => x.country),
            datasets: [
                {
                    label: "Meetings",
                    data: countryStats.map((x) => x.count),
                    backgroundColor: countryStats.map((_, i) => alpha(COLOR_PALETTE[i % COLOR_PALETTE.length], 0.75)),
                    borderColor: countryStats.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]),
                    borderWidth: 1.5,
                },
            ],
        }),
        [countryStats]
    );

    const cityChartData = useMemo(
        () => ({
            labels: cityStats.map((x) => x.city),
            datasets: [
                {
                    label: "Meetings",
                    data: cityStats.map((x) => x.count),
                    backgroundColor: cityStats.map((_, i) => alpha(COLOR_PALETTE[(i + 3) % COLOR_PALETTE.length], 0.75)),
                    borderColor: cityStats.map((_, i) => COLOR_PALETTE[(i + 3) % COLOR_PALETTE.length]),
                    borderWidth: 1.5,
                },
            ],
        }),
        [cityStats]
    );

    const statusChartData = useMemo(
        () => ({
            labels: statusStats.map((x) => x.status),
            datasets: [
                {
                    label: "Meetings",
                    data: statusStats.map((x) => x.count),
                    backgroundColor: statusStats.map((_, i) => alpha(COLOR_PALETTE[(i + 6) % COLOR_PALETTE.length], 0.75)),
                    borderColor: statusStats.map((_, i) => COLOR_PALETTE[(i + 6) % COLOR_PALETTE.length]),
                    borderWidth: 1.5,
                },
            ],
        }),
        [statusStats]
    );

    const smallChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.formattedValue}` } },
        },
    };

    const clearAll = () => {
        setRange("all");
        setActiveCustomer("");
        setSearch("");
        setType("all");
        setStartDate(null);
        setEndDate(null);
        setSelSalesReps([]);
        setSelTypes([]);
        setSelStatuses([]);
        setSelCountries([]);
        setSelCities([]);
        setTblCustomer("");
        setTblRep("");
        setTblStatus("");
        setTableMode("health");
        setSelWeeks([]);
    };

    const hasChartFilters = selCountries.length || selCities.length || selStatuses.length;

    const clearChartFilters = () => {
        setSelCountries([]);
        setSelCities([]);
        setSelStatuses([]);
        setSelWeeks([]);
    };

    const chipBarSx = {
        mb: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${alpha("#1976d2", 0.18)}`,
        background: alpha("#1976d2", 0.06),
    };

    const chipSx = {
        height: 26,
        fontSize: 12,
        "& .MuiChip-label": { px: 1 },
    };



    const SelectedFiltersBar = () => {
        if (!hasChartFilters) return null;

        return (
            <Box sx={chipBarSx}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
                        Selected:
                    </Typography>

                    {selCountries.map((c) => (
                        <Chip
                            key={`country-${c}`}
                            size="small"
                            sx={chipSx}
                            label={`Country: ${c}`}
                            onDelete={() => setSelCountries((prev) => prev.filter((x) => x !== c))}
                        />
                    ))}

                    {selCities.map((c) => (
                        <Chip
                            key={`city-${c}`}
                            size="small"
                            sx={chipSx}
                            label={`City: ${c}`}
                            onDelete={() => setSelCities((prev) => prev.filter((x) => x !== c))}
                        />
                    ))}

                    {selStatuses.map((s) => (
                        <Chip
                            key={`status-${s}`}
                            size="small"
                            sx={chipSx}
                            label={`Status: ${s}`}
                            onDelete={() => setSelStatuses((prev) => prev.filter((x) => x !== s))}
                        />
                    ))}

                    {selWeeks.map((w) => (
                        <Chip
                            key={`week-${w}`}
                            size="small"
                            sx={chipSx}
                            label={`Week: ${w}`}
                            onDelete={() =>
                                setSelWeeks((prev) => prev.filter((x) => x !== w))
                            }
                        />
                    ))}


                    <Box sx={{ flexGrow: 1 }} />

                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<ClearIcon />}
                        onClick={clearChartFilters}
                        sx={cleanBtnSx}
                    >
                        Clear Chart Filters
                    </Button>

                </Stack>
            </Box>
        );
    };


    return (
        <Box sx={{ pb: 3 }}>
            <Box
                sx={{
                    mb: 2.5,
                    px: 2,
                    py: 2,
                    borderRadius: "10px",
                    background: "#FFFFFF",
                    border: "1px solid rgba(15, 23, 42, 0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <InsertChartOutlinedIcon />
                    <Typography variant="h6" fontWeight={700}>
                        Meeting Dashboard
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<ClearIcon />}
                        onClick={clearAll}
                        sx={{ backgroundColor: "#FFFFFF", color: "#101828 !important", border: "1px solid rgba(15, 23, 42, 0.12)", borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none", "&:hover": { backgroundColor: "#FFFFFF", borderColor: "#175CD3", color: "#175CD3 !important", boxShadow: "none" } }}
                    >
                        Clear
                    </Button>

                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadExcel(tableFiltered)}
                        sx={{ backgroundColor: "#0B1F3A", color: "#FFFFFF !important", borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none", "&:hover": { backgroundColor: "#12345B", boxShadow: "none" } }}
                    >
                        Download
                    </Button>

                    <IconButton onClick={fetchMeetings} title="Refresh">
                        <RefreshIcon />
                    </IconButton>
                </Stack>
            </Box>

            {/* KPI kartları */}
            <Grid container spacing={2} sx={{ mb: 1.5 }}>
                {loading ? (
                    [0, 1, 2, 3].map((i) => (
                        <Grid key={i} item xs={12} sm={6} md={3}>
                            <Skeleton variant="rounded" height={96} />
                        </Grid>
                    ))
                ) : (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                onClick={onTotalMeetingClick}
                                sx={{
                                    cursor: "pointer",
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    border: range === "all" ? "2px solid #0288d1" : "2px solid transparent",
                                    transition: "all .2s ease",
                                    "&:hover": { transform: "scale(1.03)" },
                                }}
                            >
                                <CardContent sx={{ p: 2.25 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha("#0288d1", 0.12), color: "#0288d1" }}>
                                            <InsertChartOutlinedIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} color="#0288d1">
                                                Total Meeting
                                            </Typography>
                                            <Typography variant="h5" fontWeight={800}>
                                                {kpis.total}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Avg. Days: {kpis.avgDays}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>


                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                onClick={onRecentClick}
                                sx={{
                                    cursor: "pointer",
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    border: isActiveRange("0-30") ? "2px solid #2e7d32" : "2px solid transparent",
                                    transition: "all .2s ease",
                                    "&:hover": { transform: "scale(1.03)" },
                                }}
                            >
                                <CardContent sx={{ p: 2.25 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha("#2e7d32", 0.12), color: "#2e7d32" }}>
                                            <EventAvailableIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} color="#2e7d32">
                                                0–30 Days
                                            </Typography>
                                            <Typography variant="h5" fontWeight={800}>
                                                {kpis.within30}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Last meeting is recent
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>


                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                onClick={onCriticalClick}
                                sx={{
                                    cursor: "pointer",
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    border: isActiveRange("90+") ? "2px solid #b71c1c" : "2px solid transparent",
                                    transition: "all .2s ease",
                                    "&:hover": { transform: "scale(1.03)" },
                                }}
                            >
                                <CardContent sx={{ p: 2.25 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha("#b71c1c", 0.12), color: "#b71c1c" }}>
                                            <WarningAmberIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} color="#b71c1c">
                                                90+ Days
                                            </Typography>
                                            <Typography variant="h5" fontWeight={800}>
                                                {kpis.over90}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Action is required
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>


                        <Grid item xs={12} sm={6} md={3}>
                            <Card
                                onClick={onNoDateClick}
                                sx={{
                                    cursor: "pointer",
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    border: isActiveRange("no-date") ? "2px solid #9e9e9e" : "2px solid transparent",
                                    transition: "all .2s ease",
                                    "&:hover": { transform: "scale(1.03)" },
                                }}
                            >
                                <CardContent sx={{ p: 2.25 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha("#9e9e9e", 0.15), color: "#616161" }}>
                                            <HourglassBottomIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} color="#616161">
                                                No Date
                                            </Typography>
                                            <Typography variant="h5" fontWeight={800}>
                                                {kpis.noDate}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Date should be added
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                    </>
                )}
            </Grid>

            {/* Filters row */}
            <Grid container spacing={1.5} alignItems="center" mt={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3} lg={3} >
                    <TextField
                        fullWidth
                        size="small"
                        label="Search (Customer / Rep / Note / Status)"
                        placeholder="Exp: Customer, Rep…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={unifiedInputSx}
                        InputProps={{
                            startAdornment: (
                                <FilterAltOutlinedIcon fontSize="small" sx={{ mr: 1, color: "text.disabled" }} />
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2} lg={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(v) => setStartDate(v)}
                            slotProps={{ textField: { fullWidth: true, size: "small", sx: unifiedInputSx } }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6} md={2} lg={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(v) => setEndDate(v)}
                            slotProps={{ textField: { fullWidth: true, size: "small", sx: unifiedInputSx } }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6} md={2} lg={2}>
                    <TextField
                        select
                        multiple
                        fullWidth
                        size="small"
                        label="Meeting Owner"
                        value={selSalesReps}
                        onChange={(e) =>
                            setSelSalesReps(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
                        }
                        SelectProps={{
                            renderValue: (selected) => renderChips(selected, (v) => setSelSalesReps(selSalesReps.filter((x) => x !== v))),
                            MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
                        }}
                        sx={unifiedInputSx}
                    >
                        {allSalesReps.map((n) => (
                            <MenuItem key={n} value={n}>
                                <Checkbox checked={selSalesReps.indexOf(n) > -1} />
                                <ListItemText primary={n} />
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={1.5} lg={1.5}>
                    <TextField
                        select
                        multiple
                        fullWidth
                        size="small"
                        label="Meeting Type"
                        value={selTypes}
                        onChange={(e) =>
                            setSelTypes(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
                        }
                        SelectProps={{
                            renderValue: (selected) => renderChips(selected, (v) => setSelTypes(selTypes.filter((x) => x !== v))),
                            MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
                        }}
                        sx={unifiedInputSx}
                    >
                        {allTypes.map((t) => (
                            <MenuItem key={t} value={t}>
                                <Checkbox checked={selTypes.indexOf(t) > -1} />
                                <ListItemText primary={t} />
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={1.5} lg={1.5}>
                    <TextField
                        select
                        multiple
                        fullWidth
                        size="small"
                        label="Status"
                        value={selStatuses}
                        onChange={(e) =>
                            setSelStatuses(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
                        }
                        SelectProps={{
                            renderValue: (selected) => renderChips(selected, (v) => setSelStatuses(selStatuses.filter((x) => x !== v))),
                            MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
                        }}
                        sx={unifiedInputSx}
                    >
                        {allStatuses.map((s) => (
                            <MenuItem key={s} value={s}>
                                <Checkbox checked={selStatuses.indexOf(s) > -1} />
                                <ListItemText primary={s} />
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>
            <SelectedFiltersBar />
            {/* Range chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                {BUCKETS.map((b) => {
                    const active = range === b.key;
                    return (
                        <Chip
                            key={b.key}
                            label={b.label}
                            onClick={() => setRange((prev) => (prev === b.key ? "all" : b.key))}
                            sx={{
                                cursor: "pointer",
                                bgcolor: active ? alpha(b.color, 0.16) : alpha(b.color, 0.08),
                                color: "#111",
                                border: `1px solid ${alpha(b.color, 0.25)}`,
                            }}
                        />
                    );
                })}
            </Stack>

            {loading ? (
                <Stack spacing={2}>
                    <Skeleton variant="rounded" height={280} />
                    <Skeleton variant="rounded" height={420} />
                    <Skeleton variant="rounded" height={280} />
                </Stack>
            ) : errorMsg ? (
                <Card sx={{ borderLeft: "4px solid #b71c1c", borderRadius: "10px" }}>
                    <CardContent>
                        <Typography color="error" fontWeight={600}>
                            {errorMsg}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* 2 charts */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Daily Distribution
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Click on the column
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ height: 260 }}>
                                        <Bar data={barData} options={{ ...chartOptions, onClick: (_evt, els) => handleBucketClick(els) }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Daily Rates
                                    </Typography>
                                    <Box sx={{ height: 260, maxWidth: 420, mx: "auto" }}>
                                        <Doughnut
                                            data={doughnutData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: { legend: { position: "bottom" } },
                                                cutout: "65%",
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                    </Grid>

                    {/* ✅ 3 donuts: country/city/status (click => filters) */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Country Distribution
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Based on meeting data
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ height: 260 }}>
                                        <Doughnut
                                            data={countryChartData}
                                            options={{
                                                ...smallChartOptions,
                                                onClick: (evt, els) => {
                                                    const idx = els?.[0]?.index;
                                                    if (idx == null) return;
                                                    const label = countryChartData.labels[idx];
                                                    setSelCountries((prev) => applyChartClickSelection(label, prev, evt));
                                                    setTableMode("health");
                                                    setActiveCustomer("");
                                                    scrollToTable();
                                                },
                                                plugins: {
                                                    legend: { position: "bottom" },
                                                    tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.formattedValue} meeting(s)` } },
                                                },
                                                cutout: "65%",
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            City Distribution
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Based on meeting data
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ height: 260 }}>
                                        <Doughnut
                                            data={cityChartData}
                                            options={{
                                                ...smallChartOptions,
                                                onClick: (evt, els) => {
                                                    const idx = els?.[0]?.index;
                                                    if (idx == null) return;
                                                    const label = cityChartData.labels[idx];
                                                    setSelCities((prev) => applyChartClickSelection(label, prev, evt));
                                                    setTableMode("health");
                                                    setActiveCustomer("");
                                                    scrollToTable();
                                                },
                                                plugins: {
                                                    legend: { position: "bottom" },
                                                    tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.formattedValue} meeting(s)` } },
                                                },
                                                cutout: "65%",
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Status Distribution
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Based on meeting data
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ height: 260 }}>
                                        <Doughnut
                                            data={statusChartData}
                                            options={{
                                                ...smallChartOptions,
                                                onClick: (evt, els) => {
                                                    const idx = els?.[0]?.index;
                                                    if (idx == null) return;
                                                    const label = statusChartData.labels[idx];
                                                    setSelStatuses((prev) => applyChartClickSelection(label, prev, evt));
                                                    setTableMode("health");
                                                    setActiveCustomer("");
                                                    scrollToTable();
                                                },
                                                plugins: {
                                                    legend: { position: "bottom" },
                                                    tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.formattedValue} meeting(s)` } },
                                                },
                                                cutout: "65%",
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Customer Bar */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Number of Meetings by Customer
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Click on the bar
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ height: 420 }}>
                                        <Bar
                                            data={customerChartData}
                                            options={{ ...customerChartOptions, onClick: (_evt, els) => handleCustomerBarClick(els) }}
                                            plugins={[customerHoverPlugin]}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ mb: 1 }}
                                    >
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Meetings by Week (Meeting Date)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            All Weeks
                                        </Typography>
                                    </Stack>

                                    <Box sx={{ height: 280 }}>
                                        <Bar
                                            data={weeklyMeetingChartData}
                                            options={{
                                                ...weeklyMeetingChartOptions,
                                                onClick: (evt, els) => {
                                                    const idx = els?.[0]?.index;
                                                    if (idx == null) return;

                                                    const label = weeklyMeetingChartData.labels[idx];

                                                    setSelWeeks((prev) =>
                                                        applyChartClickSelection(label, prev, evt)
                                                    );

                                                    setTableMode("health");
                                                    setActiveCustomer("");
                                                    scrollToTable();
                                                },
                                            }}
                                        />

                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>


                    {/* Table */}
                    <div ref={tableRef} />
                    <Card sx={{ borderRadius: "10px", boxShadow: "none" }}>
                        <CardContent sx={{ p: 0 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {tableMode === "health"
                                        ? `Customers (${tableFiltered.length})`
                                        : `Meetings of ${activeCustomer} (${tableFiltered.length})`}
                                </Typography>
                                {tableMode === "health" && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ px: 2, pb: 1, display: "block" }}
                                    >
                                        ℹ️ For each customer, only the most recent meeting details are displayed.
                                    </Typography>
                                )}

                                <Stack direction="row" spacing={1}>
                                    {(range !== "all" ||
                                        activeCustomer ||
                                        startDate ||
                                        endDate ||
                                        selSalesReps.length ||
                                        selTypes.length ||
                                        selStatuses.length ||
                                        selCountries.length ||
                                        selCities.length ||
                                        tblCustomer ||
                                        tblRep ||
                                        tblStatus) && (
                                        <Button size="small" variant="contained" startIcon={<ClearIcon />} onClick={clearAll} sx={cleanBtnSx}>
                                            Clean the Filters
                                        </Button>
                                    )}

                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => downloadExcel(tableFiltered)}
                                        sx={downloadBtnSx}
                                    >
                                        Download
                                    </Button>
                                </Stack>
                            </Stack>

                            {/* Table internal filters */}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ px: 2, pb: 1 }}>
                                <TextField
                                    size="small"
                                    label="Filter: Customer"
                                    value={tblCustomer}
                                    onChange={(e) => setTblCustomer(e.target.value)}
                                    sx={tblInputSx}
                                />

                                <TextField
                                    size="small"
                                    label="Filter: Meeting Owner"
                                    value={tblRep}
                                    onChange={(e) => setTblRep(e.target.value)}
                                    sx={tblInputSx}
                                />

                                <TextField
                                    select
                                    size="small"
                                    label="Status"
                                    value={tblStatus}
                                    onChange={(e) => setTblStatus(e.target.value)}
                                    sx={tblInputSx}
                                >
                                    <MenuItem value="">
                                        <em>All</em>
                                    </MenuItem>
                                    {allStatuses.map((s) => (
                                        <MenuItem key={s} value={(s || "").toLowerCase()}>
                                            {s}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>

                            <Box sx={{ maxHeight: 460, overflow: "auto", px: 2, pb: 1 }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                                    <thead>
                                    <tr>
                                        <Th>Customer</Th>
                                        <Th>Meeting Title</Th>
                                        <Th>Type</Th>
                                        <Th>Status</Th>
                                        <Th>Platform</Th>
                                        <Th>Last Meeting</Th>
                                        <Th align="right">Days</Th>
                                        <Th>Meeting Owner</Th>
                                        <Th align="center">Range</Th>
                                        <Th align="center">Action</Th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {pagedRows.map((r, i) => {
                                        const st = statusStyle(r.meetingStatus);
                                        const tt = typeStyle(r.meetingType);

                                        return (
                                            <tr
                                                key={r.id}
                                                style={{
                                                    borderLeft: `4px solid ${r.color}`,
                                                    background: i % 2 ? "rgba(0,0,0,0.015)" : "transparent",
                                                    transition: "background .15s ease",
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.background = alpha("#1976d2", 0.04))}
                                                onMouseLeave={(e) =>
                                                    (e.currentTarget.style.background =
                                                        i % 2 ? "rgba(0,0,0,0.015)" : "transparent")
                                                }
                                            >
                                                <Td>
                                                    {r.customer ? (
                                                        <Tooltip title="Edit Meeting" arrow>
                                                            <RouterLink to={`/meeting/create-meeting/details?id=${r.id}`} style={{ textDecoration: "none" }}>
                                  <span
                                      style={{
                                          color: "#1976d2",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                          fontWeight: 500,
                                      }}
                                  >
                                    {r.customer.length > 50 ? `${r.customer.slice(0, 47)}...` : r.customer}
                                  </span>
                                                            </RouterLink>
                                                        </Tooltip>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </Td>

                                                <Td>
                                                    {r.meetingTitle ? (
                                                        <Tooltip title={r.meetingTitle} arrow>
                                                            <RouterLink to={`/meeting/create-meeting/details?id=${r.id}`} style={{ textDecoration: "none" }}>
                                  <span
                                      style={{
                                          color: "#ff7400",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                          fontWeight: 450,
                                      }}
                                  >
                                    {r.meetingTitle.length > 30 ? `${r.meetingTitle.slice(0, 27)}...` : r.meetingTitle}
                                  </span>
                                                            </RouterLink>
                                                        </Tooltip>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </Td>

                                                <Td>
                                                    {r.meetingType ? (
                                                        <Chip size="small" label={r.meetingType} sx={{ bgcolor: tt.bg, color: tt.fg, border: `1px solid ${tt.br}` }} />
                                                    ) : (
                                                        "—"
                                                    )}
                                                </Td>

                                                <Td>
                                                    {r.meetingStatus ? (
                                                        <Chip size="small" label={r.meetingStatus} sx={{ bgcolor: st.bg, color: st.fg, border: `1px solid ${st.br}` }} />
                                                    ) : (
                                                        "—"
                                                    )}
                                                </Td>

                                                <Td>{r.meetingPlatform || "—"}</Td>
                                                <Td>{formatDate(r.meetingDate)}</Td>
                                                <Td align="right">{Number.isFinite(r.days) ? r.days : "—"}</Td>
                                                <Td>{r.salesRepNames.length ? r.salesRepNames.join(", ") : "—"}</Td>

                                                <Td align="center">
                                                    <Chip
                                                        size="small"
                                                        label={BUCKETS.find((b) => b.key === r.bucket)?.label || "—"}
                                                        sx={{
                                                            bgcolor: alpha(r.color, 0.18),
                                                            color: "#111",
                                                            border: `1px solid ${alpha(r.color, 0.35)}`,
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Td>

                                                <Td align="center">
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            component={RouterLink}
                                                            to={`/meeting/create-meeting/details?id=${r.id}`}
                                                            target="_blank"
                                                            rel="noopener"
                                                            color="primary"
                                                        >
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Td>
                                            </tr>
                                        );
                                    })}

                                    {tableFiltered.length === 0 && (
                                        <tr>
                                            <td colSpan={10} style={{ padding: 28, textAlign: "center", color: "#757575" }}>
                                                No records matching the filters were found.
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </Box>

                            <Box sx={{ px: 2, pb: 1 }}>
                                <TablePagination
                                    component="div"
                                    count={tableFiltered.length}
                                    page={page}
                                    onPageChange={(_e, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
}
